import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "@/app/pages/Docs/QuickStartShared";

const SAMPLE_REQUEST = `{
  "hotline_id": "local.delegated-execution.workspace-summary.v1",
  "input": {
    "text": "summarize my workspace"
  }
}`;

const SAMPLE_SUCCESS = `{
  "request_id": "req_xxx",
  "status": "SUCCEEDED",
  "result_package": {
    "request_id": "req_xxx",
    "responder_id": "responder_xxx",
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "ok",
    "output": {
      "summary": "..."
    },
    "timing": {
      "elapsed_ms": 123
    }
  }
}`;

const SAMPLE_ERROR = `{
  "request_id": "req_xxx",
  "status": "FAILED",
  "result_package": {
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "error",
    "error": {
      "code": "AUTH_TOKEN_INVALID",
      "message": "task token is missing or invalid"
    }
  }
}`;

const SAMPLE_REGISTRATION = `{
  "hotline_id": "com.example.workspace-summary.v1",
  "summary": "Summarize one workspace snapshot for an external Caller.",
  "input_schema": {
    "type": "object",
    "properties": {
      "workspace_path": { "type": "string" }
    },
    "required": ["workspace_path"]
  },
  "output_schema": {
    "type": "object",
    "properties": {
      "summary": { "type": "string" }
    },
    "required": ["summary"]
  },
  "examples": [
    {
      "input": { "workspace_path": "/tmp/demo" },
      "output": { "summary": "..." }
    }
  ]
}`;

export default function EnglishProtocolExamplesPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <QuickStartHero
        role="Protocol · Examples · EN"
        roleColor="#3B82F6"
        titleTop="Minimal protocol"
        titleBottom="examples"
        lead="This page does not try to teach the full runtime. It only collects the four most stable protocol artifacts: a minimal request, a successful result_package, one common error shape, and a Hotline registration payload."
        meta={["Request", "result_package", "Error", "Registration"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">WHO THIS PAGE IS FOR</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">For readers who want the shapes before the walkthrough</h2>
          <p className="mt-4 text-sm font-bold leading-7 text-zinc-800">
            If you already understand the product framing but want one stable page you can cite for request, result, error, and registration shapes, this is the shortest entrypoint.
          </p>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="Minimal request"
            intent="The first caller-side question is usually not about every runtime detail. It is about the minimum fields required to make a Hotline call."
          >
            <CodeBlock language="json" caption="Minimal request shape">{SAMPLE_REQUEST}</CodeBlock>
            <Note label="Note">
              The outer call pattern stays stable. Only the capability-specific <code className="font-mono">input</code> shape changes with each Hotline schema.
            </Note>
          </Step>

          <Step
            num="02"
            title="Successful result_package"
            intent="One of the core protocol advantages is that many different Hotlines still return the same outer result envelope."
          >
            <CodeBlock language="json" caption="Successful result_package">{SAMPLE_SUCCESS}</CodeBlock>
            <div className="border-2 border-black bg-white/80 p-4 text-sm font-bold text-zinc-800">
              <p>Fields to inspect first:</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                <li>· <code className="font-mono">status</code>: request lifecycle status</li>
                <li>· <code className="font-mono">result_package.status</code>: protocol-level ok or error</li>
                <li>· <code className="font-mono">hotline_id</code>: exact capability version</li>
                <li>· <code className="font-mono">timing.elapsed_ms</code>: observability and settlement input</li>
              </ul>
            </div>
          </Step>

          <Step
            num="03"
            title="Common error shape"
            intent="Answer engines and implementation guides both need failure examples, not only success examples. Agents need to know when to stop, retry, or escalate."
          >
            <CodeBlock language="json" caption="Common error shape">{SAMPLE_ERROR}</CodeBlock>
            <Note variant="warn" label="Warning">
              Read the structured <code className="font-mono">error.code</code> first. It is more automation-friendly than free-form error text.
            </Note>
          </Step>

          <Step
            num="04"
            title="Minimal Hotline registration payload"
            intent="Responder-side onboarding usually fails on contract quality before it fails on runtime cleverness. A minimal payload makes that obvious."
          >
            <CodeBlock language="json" caption="Minimal registration payload">{SAMPLE_REGISTRATION}</CodeBlock>
            <Note label="Focus">
              The fields that matter most are <code className="font-mono">hotline_id</code>, <code className="font-mono">summary</code>,
              <code className="font-mono">input_schema</code>, <code className="font-mono">output_schema</code>, and examples.
            </Note>
          </Step>
        </div>

        <div className="mt-12 border-[6px] border-black bg-[#3B82F6]/15 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">Where to go next</h2>
          <p className="mt-3 text-sm font-bold text-zinc-900 sm:text-base">
            This page teaches the shapes. The quick starts teach the runnable path and the current proof workflow.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <NextCta href="/en/docs/quick-start-caller/" label="Caller Quick Start" tone="lime" />
            <NextCta href="/en/docs/quick-start-responder/" label="Responder Quick Start" tone="blue" />
            <NextCta href="/en/docs/" label="Docs Overview" tone="white" />
          </div>
        </div>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
